import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatDateTimeCompact } from "@/utils/relative-time";

import { BID_STATUS, type SchoolBid } from "../../../BidRequests/types";
import { formatMoney } from "../../../BidRequests/utils";

interface BidComparisonCardProps {
  bid: SchoolBid;
  onViewBid: (bid: SchoolBid) => void;
}

export const BidComparisonCard = ({ bid, onViewBid }: BidComparisonCardProps) => {
  const { t } = useTranslation();
  const isWon = bid.status === BID_STATUS.won;
  const bidDate = formatDateTimeCompact(bid.updated_at ?? bid.submitted_at);

  return (
    <Card className={cn("gap-0 py-0", isWon && "border-success-500")}>
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <p className="text-muted-foreground text-xs">
            {bidDate
              ? t("bidRequests.detail.bidSubmittedOn", { date: bidDate })
              : t("bidRequests.detail.bid")}
          </p>
          <p className="font-semibold">{bid.aggregator_name}</p>
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold text-lg">{formatMoney(bid.currency, bid.total)}</p>
            {bid.is_recommended && <Badge>{t("bidRequests.detail.bestPrice")}</Badge>}
            {isWon && <Badge variant="success">{t("bidRequests.detail.accepted")}</Badge>}
          </div>
        </div>
        <WFPButton variant="secondary" className="shrink-0" onClick={() => onViewBid(bid)}>
          {t("bidRequests.detail.viewBid")}
        </WFPButton>
      </CardContent>
    </Card>
  );
};
